/**
 * Admin Featured Stores Management
 * Allows admin to add, remove, reorder, and upload logos for featured stores
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Link2, 
  ArrowLeft, 
  Store,
  Plus,
  Trash2,
  RefreshCw,
  Star,
  MapPin,
  Eye,
  EyeOff,
  Sparkles,
  Upload,
  Image,
  X
} from 'lucide-react';

const AdminFeaturedStores = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [featuredStores, setFeaturedStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputRefs = useRef({});
  
  // Add store form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newStore, setNewStore] = useState({
    store_name: '',
    location: '',
    rating: 4.5,
    store_logo: ''
  });

  useEffect(() => {
    if (!isAdmin()) {
      toast.error('Access denied');
      navigate('/dashboard');
      return;
    }
    fetchFeaturedStores();
  }, [isAdmin, navigate]);

  const fetchFeaturedStores = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/featured-stores/?include_inactive=true');
      setFeaturedStores(response.data || []);
    } catch (error) {
      console.error('Error fetching featured stores:', error);
      toast.error('Failed to load featured stores');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const response = await api.post('/admin/featured-stores/seed-defaults');
      toast.success(response.data.message);
      fetchFeaturedStores();
    } catch (error) {
      toast.error('Failed to seed default stores');
    }
  };

  const handleAddStore = async () => {
    if (!newStore.store_name.trim()) {
      toast.error('Store name is required');
      return;
    }

    setSaving(true);
    try {
      // First create the store entry
      const response = await api.post('/admin/featured-stores/', {
        store_id: `custom-${Date.now()}`
      });
      
      // Then update with the custom details
      await api.put(`/admin/featured-stores/${response.data.id}`, null, {
        params: {
          store_name: newStore.store_name,
          location: newStore.location || null,
          rating: newStore.rating,
          store_logo: newStore.store_logo || null
        }
      });
      
      toast.success('Store added to featured list');
      setShowAddForm(false);
      setNewStore({ store_name: '', location: '', rating: 4.5, store_logo: '' });
      fetchFeaturedStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add store');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveStore = async (featuredId) => {
    if (!window.confirm('Remove this store from featured list?')) return;
    
    try {
      await api.delete(`/admin/featured-stores/${featuredId}`);
      toast.success('Store removed from featured list');
      fetchFeaturedStores();
    } catch (error) {
      toast.error('Failed to remove store');
    }
  };

  const handleToggleActive = async (store) => {
    try {
      await api.put(`/admin/featured-stores/${store.id}`, null, {
        params: { is_active: !store.is_active }
      });
      toast.success(store.is_active ? 'Store hidden' : 'Store visible');
      fetchFeaturedStores();
    } catch (error) {
      toast.error('Failed to update store');
    }
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    
    const newOrder = [...featuredStores];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    
    try {
      await api.post('/admin/featured-stores/reorder', newOrder.map(s => s.id));
      setFeaturedStores(newOrder);
    } catch (error) {
      toast.error('Failed to reorder');
    }
  };

  const handleMoveDown = async (index) => {
    if (index === featuredStores.length - 1) return;
    
    const newOrder = [...featuredStores];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    
    try {
      await api.post('/admin/featured-stores/reorder', newOrder.map(s => s.id));
      setFeaturedStores(newOrder);
    } catch (error) {
      toast.error('Failed to reorder');
    }
  };

  const handleLogoUpload = async (storeId, file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Use JPEG, PNG, WebP, or GIF');
      return;
    }
    
    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large. Max 2MB');
      return;
    }
    
    setUploadingId(storeId);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(
        `/admin/featured-stores/${storeId}/upload-logo`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success('Logo uploaded successfully');
      fetchFeaturedStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingId(null);
    }
  };

  const handleDeleteLogo = async (storeId) => {
    if (!window.confirm('Delete this store logo?')) return;
    
    try {
      await api.delete(`/admin/featured-stores/${storeId}/logo`);
      toast.success('Logo deleted');
      fetchFeaturedStores();
    } catch (error) {
      toast.error('Failed to delete logo');
    }
  };

  const triggerFileInput = (storeId) => {
    if (fileInputRefs.current[storeId]) {
      fileInputRefs.current[storeId].click();
    }
  };

  // Get full logo URL
  const getLogoUrl = (logoPath) => {
    if (!logoPath) return null;
    if (logoPath.startsWith('http')) return logoPath;
    return `${process.env.REACT_APP_BACKEND_URL}${logoPath}`;
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin')}
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-white hover:bg-slate-800"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-3">
              <Link2 className="h-7 w-7 text-amber-500" />
              <span className="text-xl font-bold text-white tracking-tight">
                
              </span>
              <span className="text-xs font-mono text-amber-400 bg-amber-500/10 px-2 py-1 rounded border border-amber-500/20">
                FEATURED STORES
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleSeedDefaults}
              variant="outline"
              size="sm"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Seed Defaults
            </Button>
            <Button
              onClick={() => setShowAddForm(true)}
              className="bg-amber-500 text-slate-950 hover:bg-amber-400"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Store
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white tracking-tight mb-2">
            Featured Stores Management
          </h1>
          <p className="text-slate-400">
            Manage the stores displayed on the homepage. Upload logos, reorder, toggle visibility, or remove stores.
          </p>
        </div>

        {/* Add Store Form Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-900 border border-slate-800 rounded-lg w-full max-w-md p-6">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Store className="h-5 w-5 text-amber-500" />
                Add Featured Store
              </h2>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">Store Name *</Label>
                  <Input
                    value={newStore.store_name}
                    onChange={(e) => setNewStore({ ...newStore, store_name: e.target.value })}
                    placeholder="Enter store name"
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Location</Label>
                  <Input
                    value={newStore.location}
                    onChange={(e) => setNewStore({ ...newStore, location: e.target.value })}
                    placeholder="e.g., Kingston, Montego Bay"
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Rating (1-5)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="5"
                    step="0.1"
                    value={newStore.rating}
                    onChange={(e) => setNewStore({ ...newStore, rating: parseFloat(e.target.value) })}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
              </div>
              
              <p className="text-xs text-slate-500 mt-4">
                You can upload a logo after adding the store.
              </p>
              
              <div className="flex gap-3 mt-6">
                <Button
                  onClick={() => setShowAddForm(false)}
                  variant="outline"
                  className="flex-1 border-slate-700 text-slate-300"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddStore}
                  disabled={saving}
                  className="flex-1 bg-amber-500 text-slate-950 hover:bg-amber-400"
                >
                  {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                  Add Store
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Featured Stores List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 text-amber-500 animate-spin" />
          </div>
        ) : featuredStores.length === 0 ? (
          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-12 text-center">
            <Store className="h-12 w-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No Featured Stores</h3>
            <p className="text-slate-400 mb-4">Add stores to display on the homepage</p>
            <Button
              onClick={handleSeedDefaults}
              className="bg-amber-500 text-slate-950 hover:bg-amber-400"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Seed Default Stores
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {featuredStores.map((store, index) => (
              <div
                key={store.id}
                className={`bg-slate-900/50 border rounded-lg p-4 flex items-center gap-4 transition-all ${
                  store.is_active ? 'border-slate-800' : 'border-slate-800/50 opacity-60'
                }`}
                data-testid={`featured-store-row-${store.id}`}
              >
                {/* Order Controls */}
                <div className="flex flex-col items-center gap-1">
                  <button
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                    className="text-slate-500 hover:text-white disabled:opacity-30"
                  >
                    ▲
                  </button>
                  <span className="text-xs text-slate-500 font-mono">{index + 1}</span>
                  <button
                    onClick={() => handleMoveDown(index)}
                    disabled={index === featuredStores.length - 1}
                    className="text-slate-500 hover:text-white disabled:opacity-30"
                  >
                    ▼
                  </button>
                </div>

                {/* Store Logo with Upload */}
                <div className="relative group">
                  <div className="w-16 h-16 rounded-xl bg-slate-800 flex items-center justify-center overflow-hidden border border-slate-700">
                    {store.store_logo ? (
                      <img 
                        src={getLogoUrl(store.store_logo)} 
                        alt={store.store_name} 
                        className="w-full h-full object-cover" 
                      />
                    ) : (
                      <Store className="w-6 h-6 text-slate-500" />
                    )}
                  </div>
                  
                  {/* Upload overlay */}
                  <div className="absolute inset-0 bg-black/60 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      ref={el => fileInputRefs.current[store.id] = el}
                      onChange={(e) => handleLogoUpload(store.id, e.target.files[0])}
                    />
                    <button
                      onClick={() => triggerFileInput(store.id)}
                      disabled={uploadingId === store.id}
                      className="p-1.5 bg-amber-500 rounded text-slate-950 hover:bg-amber-400 disabled:opacity-50"
                      title="Upload logo"
                    >
                      {uploadingId === store.id ? (
                        <RefreshCw className="w-3 h-3 animate-spin" />
                      ) : (
                        <Upload className="w-3 h-3" />
                      )}
                    </button>
                    {store.store_logo && (
                      <button
                        onClick={() => handleDeleteLogo(store.id)}
                        className="p-1.5 bg-red-500 rounded text-white hover:bg-red-400"
                        title="Delete logo"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Store Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold truncate">{store.store_name}</h3>
                  <div className="flex items-center gap-3 text-sm text-slate-400">
                    {store.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {store.location}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                      {store.rating?.toFixed(1)}
                    </span>
                  </div>
                  {store.store_logo && (
                    <p className="text-xs text-teal-400 mt-1 flex items-center gap-1">
                      <Image className="w-3 h-3" />
                      Custom logo
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => handleToggleActive(store)}
                    variant="ghost"
                    size="sm"
                    className={store.is_active ? 'text-teal-400' : 'text-slate-500'}
                    title={store.is_active ? 'Hide store' : 'Show store'}
                  >
                    {store.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </Button>
                  <Button
                    onClick={() => handleRemoveStore(store.id)}
                    variant="ghost"
                    size="sm"
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    title="Remove from featured"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Stats */}
        {featuredStores.length > 0 && (
          <div className="mt-8 bg-slate-900/50 border border-slate-800 rounded-lg p-4">
            <div className="flex items-center justify-between text-sm flex-wrap gap-2">
              <span className="text-slate-400">
                Total: <span className="text-white font-medium">{featuredStores.length}</span> stores
              </span>
              <span className="text-slate-400">
                Active: <span className="text-teal-400 font-medium">
                  {featuredStores.filter(s => s.is_active).length}
                </span>
              </span>
              <span className="text-slate-400">
                With Logo: <span className="text-amber-400 font-medium">
                  {featuredStores.filter(s => s.store_logo).length}
                </span>
              </span>
              <span className="text-slate-400">
                Hidden: <span className="text-slate-500 font-medium">
                  {featuredStores.filter(s => !s.is_active).length}
                </span>
              </span>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="mt-8 bg-slate-900/30 border border-slate-800/50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-slate-300 mb-2">Quick Guide</h3>
          <ul className="text-xs text-slate-500 space-y-1">
            <li>• <strong>Upload Logo:</strong> Hover over the store icon and click the upload button</li>
            <li>• <strong>Reorder:</strong> Use the up/down arrows to change display order</li>
            <li>• <strong>Hide/Show:</strong> Click the eye icon to toggle visibility on homepage</li>
            <li>• <strong>Delete:</strong> Click the trash icon to remove from featured list</li>
            <li>• <strong>Logo Size:</strong> Max 2MB, formats: JPEG, PNG, WebP, GIF</li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default AdminFeaturedStores;
